import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui/components/ui/alert-dialog";
import { useState, useTransition } from "react";
import { ActionButton } from "@/shared/common/action-button";

/** A transaction handle, as every data-store intent returns one. */
type Tx = { isPersisted: { promise: Promise<unknown> } };

export function EditLink({
  canEdit,
  to,
  params,
  search,
}: {
  canEdit: boolean;
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
}) {
  return (
    <ActionButton state={{ allowed: canEdit }} variant="ghost" size="sm" to={to} params={params} search={search}>
      Edit
    </ActionButton>
  );
}

/**
 * The toolbar pair for a builder session. `saveLabel` differs by kind and by
 * whether the definition exists yet — "Create" on a new one, "Save" on an edit.
 *
 * Save stays disabled until something changes. Every builder gated on dirty
 * before the shell existed, and for good reason: these definitions are
 * versioned, so a stray click on an untouched page burns a revision.
 */
export function SaveCancelActions({
  canEdit,
  isDirty,
  isSaving,
  saveLabel = "Save",
  onSave,
  onCancel,
}: {
  canEdit: boolean;
  isDirty: boolean;
  isSaving?: boolean;
  saveLabel?: string;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <ActionButton state={{ allowed: true }} variant="ghost" size="sm" onClick={onCancel}>
        Cancel
      </ActionButton>
      <ActionButton state={{ allowed: canEdit && isDirty && !isSaving }} size="sm" onClick={onSave}>
        {isSaving ? "Saving..." : saveLabel}
      </ActionButton>
    </>
  );
}

interface DeactivateActionProps {
  canEdit: boolean;
  /** Retired definitions offer Activate instead; nothing is ever hard-deleted here. */
  isRetired: boolean;
  /** Quoted back in the confirmation, so the clinician sees which one they are retiring. */
  name: string;
  /** Programs say Archive where protocols say Deactivate. */
  verb?: "Deactivate" | "Archive";
  onDeactivate: () => Tx;
  onActivate: () => Tx;
  onDeactivated?: () => void;
}

/**
 * The deactivate / activate pair with its confirmation. Retyped in three
 * detail views before this; the wording differed in each of them.
 */
export function DeactivateAction({
  canEdit,
  isRetired,
  name,
  verb = "Deactivate",
  onDeactivate,
  onActivate,
  onDeactivated,
}: DeactivateActionProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isPending, start] = useTransition();

  if (isRetired) {
    return (
      <ActionButton
        state={{ allowed: canEdit }}
        variant="outline"
        size="sm"
        onClick={() => {
          // Domain errors surface globally via the tRPC error link.
          onActivate().isPersisted.promise.catch(() => {});
        }}
      >
        Activate
      </ActionButton>
    );
  }

  return (
    <>
      <ActionButton
        state={{ allowed: canEdit && !isPending }}
        variant="outline"
        size="sm"
        onClick={() => setConfirmOpen(true)}
      >
        {verb}
      </ActionButton>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{verb} item?</AlertDialogTitle>
            <AlertDialogDescription>"{name}" will no longer be available for new work.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isPending}
              onClick={() =>
                start(async () => {
                  try {
                    await onDeactivate().isPersisted.promise;
                    onDeactivated?.();
                  } catch {
                    // Domain errors surface globally via the tRPC error link.
                  }
                })
              }
            >
              {isPending ? "Saving..." : verb}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
